<template>
	<div class="person-panel-list" ref="$personPanelList">
		<app-loader v-if="isLoadingAll" :style="loaderStyle"/>
		<div
			v-if="isEmpty"
			class="person-panel-list__empty"
		>
			{{ $t('contacts_empty') }}
		</div>
		<template v-else>
			<div class="person-panel-list__count">
				{{ $t('contacts_found') }}:
				<div class="person-panel-list__count-number">{{ clients?.length || 0 }}</div>
			</div>
			<template
				v-for="(client, index) in clients"
				:key="client.Id"
			>
				<clients-panel-list-item
					:client="client"
					:search-text="props.searchText"
				/>
				<div
					v-if="index < clients.length - 1"
					class="person-panel-list__divider"
				/>
			</template>
			<div
				v-if="isLoadingPartial"
				class="person-panel-list__load-partial"
			>
				<app-loader />
			</div>
		</template>
		<app-scroll-top-button
			:enabled="isScrollTopEnabled"
			@on-click="scrollToTop"
		/>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	onMounted,
	onUnmounted,
	ref,
	watch
} from "vue";
import throttle from "lodash/throttle";
import { clientsStore } from "@/store/clients";
import ClientsPanelListItem from "@/components/main/clients-panel/ClientsPanelListItem.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";

const emit = defineEmits<{
	(e: 'on-get-partial'): void,
}>()

const props = defineProps<{
	searchText: string;
}>()

const store = clientsStore()
const $personPanelList = ref<HTMLDivElement | null>(null)

const isLoadingAll = computed(() => store.clientsLoading);
const isLoadingPartial = computed(() => store.clientsPartialLoading);
const clients = computed(() => store.getClients);

const isEmpty = computed(() => !isLoadingAll.value && !clients.value?.length);

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$personPanelList,
	'[data-contact-id]',
	() => clients.value
)

const onScroll = throttle(() => {
	if (!$personPanelList.value) return

	if ($personPanelList.value.scrollTop + $personPanelList.value.offsetHeight > ($personPanelList.value.scrollHeight - 1000)) {
		if (!isLoadingPartial.value) {
			emit('on-get-partial')
		}
	}
}, 50)

onMounted(() => {
	$personPanelList.value?.addEventListener('scroll', onScroll)
})

onUnmounted(() => {
	$personPanelList.value?.removeEventListener('scroll', onScroll)
	onScroll.cancel()
})

watch(isLoadingAll, (newVal) => {
	if (newVal) {
		$personPanelList.value!.scrollTop = 0
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

</script>

<style scoped lang="scss">
.person-panel-list {
	display: flex;
	flex-direction: column;
	flex: 1;
	position: relative;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	overflow: auto;
	background-color: #fff;

	&__count {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-top: 4px;
		font-size: 12px;
		line-height: 18px;
		color: #4D4D4D;
		&-number {
			font-weight: 600;
			margin-left: 4px;
		}
	}

	&__empty {
		margin: auto;
		padding: 16px;
		text-align: center;
		font-size: 12px;
		line-height: 16px;
		color: #4D4D4D;
	}

	&__divider {
		flex-shrink: 0;
		height: 2px;
		margin-left: 8px;
		border-radius: 1px;
		background-color: #F2F2F2;
	}

	&__load-partial {
		display: flex;
		position: relative;
		width: 100%;
		min-height: 60px;
	}
}
</style>
